import React from "react";
import { useTable, Column, useSortBy } from "react-table";
import { BsSearch, BsSortDown, BsSortUp } from "react-icons/bs";
import { Oval } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import { DriverV2 } from "../../../models";
import {
  toggleShowAddDriverModal,
  showDeleteDriverConfirmationModal,
  setSelectedDriver,
  toggleShowEditDriverModal,
} from "../../DriversV2/driversV2Slice";

import "./Drivers.scss";
import { PhoneNumber } from "../../../components";

type ColumnsType = Pick<
  DriverV2,
  "email" | "name" | "phoneNumber" | "id" | "address" | "zip" | "dlExpiration" | "dotPhysicalExpiration" | "startTime"
>;

// Number of days out at which an expiration date is flagged as "expiring soon".
const EXPIRY_SOON_DAYS = 30;

// Timezone-safe date formatting — matches the DVIR tab's handling so date-only
// strings don't shift a day under UTC parsing.
const formatDate = (dateString?: string): string => {
  if (!dateString) return "-";
  const hasTime = dateString.includes("T");
  const date = new Date(hasTime ? dateString : dateString + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Pure helper mirroring DVIR's getStatusColor: maps an expiration date to a
// badge class + label based purely on how far out (or past) the date is.
const getExpiryStatus = (
  dateString?: string
): { className: string; label: string } => {
  if (!dateString) {
    return { className: "status-unset", label: "Not set" };
  }

  const hasTime = dateString.includes("T");
  const date = new Date(hasTime ? dateString : dateString + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const daysUntil = Math.floor((date.getTime() - today.getTime()) / msPerDay);

  const label = formatDate(dateString);
  if (daysUntil < 0) return { className: "status-expired", label };
  if (daysUntil <= EXPIRY_SOON_DAYS)
    return { className: "status-expiring", label };
  return { className: "status-valid", label };
};

function ExpiryBadge({ dateString }: { dateString?: string }) {
  const { className, label } = getExpiryStatus(dateString);
  return <span className={`status-badge ${className}`}>{label}</span>;
}

export function Drivers() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const drivers = useAppSelector((state) => state.driversV2.drivers) || [];
  const getDriversAPI = useAppSelector((state) => state.driversV2.getDrivers);
  const deleteDriverAPI = useAppSelector((state) => state.driversV2.deleteDriver);
  const driverToRemove = useAppSelector(
    (state) => state.driversV2.driverToRemove
  );

  const [search, setSearch] = React.useState<string>("");

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setSearch(e.target.value);
  };

  const onAddDriver = () => {
    dispatch(toggleShowAddDriverModal());
  };

  const data = React.useMemo(() => {
    if (search === "") return drivers;

    return drivers.filter(
      (d) =>
        d.email.toLowerCase().includes(search.toLowerCase()) ||
        d.name.toLowerCase().includes(search.toLowerCase()) ||
        d.phoneNumber.toLowerCase().includes(search.toLowerCase())
    );
  }, [drivers, search]);

  const columns: Array<Column<ColumnsType>> = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        Cell: (props) => <span className="driver-name">{props.value}</span>,
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Phone Number",
        accessor: "phoneNumber",
        Cell: (props) => (
          <PhoneNumber phoneNumber={props.row.original.phoneNumber} />
        ),
      },
      {
        Header: "Address",
        accessor: "address",
        Cell: (props) => (
          <span>{`${props.row.original.address}, ${props.row.original.zip}`}</span>
        ),
      },
      {
        Header: "DL Exp.",
        accessor: "dlExpiration",
        Cell: (props) => <ExpiryBadge dateString={props.value} />,
      },
      {
        Header: "DOT Physical",
        accessor: "dotPhysicalExpiration",
        Cell: (props) => <ExpiryBadge dateString={props.value} />,
      },
      {
        Header: "Start Time",
        accessor: "startTime",
        Cell: (props) => <span>{props.value || "-"}</span>,
      },
      {
        Header: "Actions",
        accessor: "id",
        Cell: (props) => {
          const { value, row } = props;

          const onEditDriver = (e: React.MouseEvent) => {
            e.stopPropagation();
            dispatch(setSelectedDriver(row.original));
            dispatch(toggleShowEditDriverModal());
          };

          const onSchedule = (e: React.MouseEvent) => {
            e.stopPropagation();
            navigate(`/drivers/${value}`);
          };

          const onRemoveDriver = (e: React.MouseEvent) => {
            e.stopPropagation();
            dispatch(showDeleteDriverConfirmationModal(row.original));
          };

          const isDeleting = deleteDriverAPI.loading && driverToRemove === value;

          return (
            <div
              className="actions-cell"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="action-buttons">
                <button className="btn btn-sm btn-view" onClick={onEditDriver}>
                  Edit
                </button>
                <button className="btn btn-sm btn-secondary" onClick={onSchedule}>
                  Schedule
                </button>
                {isDeleting ? (
                  <Oval
                    color="#131313"
                    height={20}
                    width={20}
                    secondaryColor="#FBD20A"
                  />
                ) : (
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={onRemoveDriver}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          );
        },
      },
    ],
    [dispatch, navigate, deleteDriverAPI.loading, driverToRemove]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data }, useSortBy);

  const isLoading = getDriversAPI.loading && drivers.length === 0;
  const isEmpty = !isLoading && data.length === 0;

  return (
    <div className="drivers-component">
      <div className="header-filters-section">
        <div className="header-row">
          <div className="title-section">
            <h2>
              Drivers
              <span className="title-count">{data.length}</span>
            </h2>
            <p className="subtitle">
              Manage your fleet's drivers and license compliance
            </p>
          </div>
          <button className="btn btn-primary" onClick={onAddDriver}>
            + Add Driver
          </button>
        </div>

        <div className="search-row">
          <div className="search-container">
            <BsSearch className="search-icon" />
            <input
              value={search}
              placeholder="Search by name, email, or phone"
              onChange={onInputChange}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="drivers-loading">
          <div className="loading-text">Loading drivers...</div>
        </div>
      ) : isEmpty ? (
        <div className="empty-state">
          <div className="empty-message">
            <h3>{search ? "No drivers match your search" : "No drivers yet"}</h3>
            <p>
              {search
                ? "Try adjusting your search terms."
                : "Add your first driver to start managing your fleet."}
            </p>
            {!search && (
              <button className="btn btn-primary" onClick={onAddDriver}>
                + Add Driver
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="drivers-table-container">
          <div className="table-scroll-wrapper">
            <table {...getTableProps()} className="drivers-table">
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                        <div className="header-cell">
                          {column.render("Header")}
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <BsSortDown className="sorting-icon" />
                            ) : (
                              <BsSortUp className="sorting-icon" />
                            )
                          ) : null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      onClick={() => navigate(`/drivers/${row.original.id}`)}
                      style={{ cursor: "pointer" }}
                    >
                      {row.cells.map((cell) => {
                        return (
                          <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
